import { sumDecimalStrings } from '@/lib/decimal';
import { formatDecimalString } from '@/lib/format';
import { compactMoneyParts, figureText, type FigureParts } from '@/lib/targets';
import {
  REVENUE_CURRENCIES,
  currenciesUsedBy,
  moverFor,
  revenueFor,
  todayIso,
  type Mover,
  type MoverDirection,
  type RevenueCurrency,
  type RevenueCycle,
} from '@/lib/use-revenue-analytics';
import type { RevenueTrackerRow } from '@/lib/types';

/**
 * The arithmetic and vocabulary behind **Top Movers** — everything the panel
 * needs that is not JSX.
 *
 * ⚠️ **NOTHING HERE RE-DERIVES A MOVEMENT.** `moverFor` in
 * `@/lib/use-revenue-analytics` is the one place a company's cycle-on-cycle
 * change is decided, and this module only groups, filters and orders what it
 * returns. A second copy of that arithmetic would agree on the day it was
 * written and diverge on the case nobody sets up on purpose — which here means
 * two screens disagreeing about whether a customer has been lost.
 *
 * ## 🔴 THE FIVE OUTCOMES, AND WHY FOUR OF THEM ARE NOT A PERCENTAGE
 *
 * The owner asked for *every scenario* in this tab, and the reason is
 * commercial rather than completist: a table that only ranks growth cannot show
 * the three findings somebody would act on — who **stopped**, who is **new**,
 * and who is **falling**. So the five directions are rendered as five sections
 * and never collapsed into one ranking.
 *
 * ⚠️ **`Mover.percent` IS `null` ON `new` AND `stopped`, AND NOTHING HERE MAY
 * INVENT ONE.** Growth from nothing is not *infinite%* and it is not *100%* —
 * it is not a percentage at all. It is also `null` when the previous cycle was
 * exactly `0.00`, which is a real state (a refund, a fully discounted line) and
 * not an absence. Every one of those renders as a word or an em dash.
 */

/* -------------------------------------------------------------------------
 * Dates — how far through a cycle today is
 * --------------------------------------------------------------------- */

const MS_PER_DAY = 86_400_000;

/**
 * A bare `YYYY-MM-DD` as a UTC instant.
 *
 * ⚠️ **`Date.UTC`, NEVER `new Date(string)`.** A cycle boundary carries no
 * timezone, and attaching this browser's offset to it moves the boundary across
 * a day for anybody west of Greenwich — which here would report a cycle as
 * complete a day early, or still running a day late, and quietly change which
 * pair of cycles the tab opens on.
 */
function utcDay(iso: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (match === null) return null;
  const [, year, month, day] = match;
  if (year === undefined || month === undefined || day === undefined) return null;
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}

export interface CycleProgress {
  /** True once today is past the cycle's own end date. */
  complete: boolean;
  /** True while today falls inside the window — the case the caption exists for. */
  running: boolean;
  elapsedDays: number;
  totalDays: number;
  /** `elapsedDays / totalDays`, `0`–`1`. */
  fraction: number;
}

/**
 * How far through its own window a cycle is, measured rather than assumed.
 *
 * 🔴 **THE CURRENT CYCLE IS PARTIAL AND THE READER MUST BE TOLD IN WORDS.**
 * Comparing a part-elapsed cycle against a complete one makes almost every
 * company read as declining. **That is arithmetic, not a finding** — and a Top
 * Movers table without the caption is read as a list of lost customers and
 * acted on. The figures here are what the caption says out loud.
 *
 * Returns `null` when either boundary is not a plain date, rather than guessing
 * a window: a caption that states a wrong fraction is worse than no caption,
 * because it is quoted onward.
 */
export function cycleProgress(cycle: RevenueCycle, today: string = todayIso()): CycleProgress | null {
  const start = utcDay(cycle.startDate);
  const end = utcDay(cycle.endDate);
  const now = utcDay(today);
  if (start === null || end === null || now === null || end < start) return null;

  /* Inclusive of both boundary days: a cycle running 1 Apr–31 Mar is 365 days, not 364. */
  const totalDays = Math.round((end - start) / MS_PER_DAY) + 1;
  if (now > end) {
    return { complete: true, running: false, elapsedDays: totalDays, totalDays, fraction: 1 };
  }
  if (now < start) {
    /* A cycle catalogued ahead of time. Reachable — the cycle list is admin-maintained. */
    return { complete: false, running: false, elapsedDays: 0, totalDays, fraction: 0 };
  }
  const elapsedDays = Math.round((now - start) / MS_PER_DAY) + 1;
  return {
    complete: false,
    running: true,
    elapsedDays,
    totalDays,
    fraction: totalDays === 0 ? 0 : elapsedDays / totalDays,
  };
}

/**
 * Every cycle that has finished, in date order.
 *
 * ⚠️ **DECIDED BY {@link cycleProgress}, NOT BY A SECOND DATE COMPARISON.** It
 * used to test `cycle.endDate < today` inline, which is the same answer for
 * every well-formed cycle and a DIFFERENT answer for a malformed one: an
 * unparsable boundary compares as a string and lands somewhere, where
 * `cycleProgress` returns `null` and this then declines to call it complete.
 * Refusing to claim a cycle has ended when its window cannot be measured is the
 * safe direction, and one definition of *complete* is what stops this function
 * and the tab's own captions ever disagreeing about a boundary date.
 */
export function completeCycles(
  cycles: readonly RevenueCycle[],
  today: string = todayIso(),
): RevenueCycle[] {
  return cycles.filter((cycle) => cycleProgress(cycle, today)?.complete === true);
}

/**
 * The two most recent cycles that have BOTH finished — **the DEFAULT selection
 * of the Top Movers tab**, and the reason a reader who touches nothing is never
 * shown a part-year against a full one.
 *
 * ⚠️ *SUPERSEDED, 11 September 2026: this was the FIXED BASIS of the tab, and
 * its whole argument was that fixing it made the partial-cycle warning
 * structurally unnecessary — "anything that re-introduces a cycle PICKER
 * re-introduces the trap, and must re-introduce the caption with it".* **The
 * owner asked for the picker back the same day, as a MULTI-SELECT, so the trap
 * is reachable again and the caption came back with it.** That caption is
 * {@link partialCycleNote}, and it renders the moment any SELECTED cycle has
 * not finished.
 *
 * The measurement that made the warning load-bearing is unchanged, which is why
 * it is not optional polish: on production, comparing the running 2026 - 27
 * against a complete cycle put **22 companies in Declining and 35 in Stopped**,
 * where two complete cycles over the same data give **11 and 12**. Twenty-three
 * companies read as lost customers purely because they have not been invoiced
 * yet this cycle.
 *
 * Returns nulls when fewer than two cycles have ended. ⚠️ **A running cycle may
 * NEVER be substituted for the missing side to make a default up** — that is
 * the trap, defaulted into, where a reader who chose it at least chose it.
 * {@link defaultCycleSelection} returns an EMPTY selection there and leaves the
 * choice to the reader, which is the only honest answer once a picker exists.
 */
export function latestCompletePair(
  cycles: readonly RevenueCycle[],
  today: string = todayIso(),
): { current: RevenueCycle | null; previous: RevenueCycle | null } {
  const done = completeCycles(cycles, today);
  if (done.length < 2) return { current: null, previous: null };
  return { current: done[done.length - 1] ?? null, previous: done[done.length - 2] ?? null };
}

/* -------------------------------------------------------------------------
 * 🔴 The cycle SELECTION — which cycles are columns, and which two are compared
 * --------------------------------------------------------------------- */

/**
 * What the tab opens on: the two most recent COMPLETE cycles, oldest first.
 *
 * 🔴 **A READER WHO TOUCHES NOTHING MUST SEE EXACTLY WHAT THEY SAW BEFORE THE
 * PICKER EXISTED.** The multi-select is an addition, not a change of basis — so
 * this is {@link latestCompletePair} and nothing else, and the tab carries no
 * warning at all until somebody selects an unfinished cycle.
 *
 * ⚠️ **EMPTY when fewer than two cycles have ended**, rather than reaching for
 * a running one to fill the gap. See {@link latestCompletePair}.
 */
export function defaultCycleSelection(
  cycles: readonly RevenueCycle[],
  today: string = todayIso(),
): RevenueCycle[] {
  const { current, previous } = latestCompletePair(cycles, today);
  if (current === null || previous === null) return [];
  return [previous, current];
}

/**
 * The cycles that are COLUMNS, in chronological order.
 *
 * 🔴 **THE ORDER COMES FROM THE CYCLE LIST, NEVER FROM THE ORDER OF
 * SELECTION.** This filters the caller's list rather than mapping over the
 * chosen ids, so ticking 2025-26 and then 2022-23 still renders oldest to
 * newest — and, far more importantly, {@link comparisonPairOf} still finds the
 * right two. Built the other way round, the pair would be *the last two boxes
 * ticked*: the Change column would silently compare 2022-23 against 2025-26 in
 * that click order, and nothing on screen would look wrong.
 *
 * ⚠️ **PRECONDITION: `cycles` IS ALREADY IN DATE ORDER**, which
 * `useRevenueAnalytics` guarantees by sorting on `startDate`. It is not
 * re-sorted here, for the same reason `completeCycles` does not: one definition
 * of *in date order*, in the hook, is what stops this file and that one ever
 * disagreeing. ⚠️ **NEVER ORDER BY `id`** — id 11 is 2022-23 and id 1 is
 * 2025-26, measured.
 *
 * `null` means *the reader has not chosen*, which is a THIRD state rather than
 * a spelling of "none selected": it is what makes the default derivable during
 * render instead of written into state by an effect. An effect would render the
 * empty selection once before correcting it, and that frame is the screenshot
 * somebody sends.
 */
export function resolveSelectedCycles(
  cycles: readonly RevenueCycle[],
  chosenIds: readonly number[] | null,
  today: string = todayIso(),
): RevenueCycle[] {
  if (chosenIds === null) return defaultCycleSelection(cycles, today);
  const wanted = new Set(chosenIds);
  return cycles.filter((cycle) => wanted.has(cycle.id));
}

/**
 * The two cycles the Change column, the % change column and all five outcomes
 * are about: **the newest selected cycle against the one immediately before
 * it.**
 *
 * 🔴 **WITH THREE OR MORE COLUMNS A READER CANNOT TELL WHICH PAIR THIS IS, AND
 * WILL ASSUME NEWEST-VERSUS-OLDEST.** That is a wrong number wearing a
 * plausible label — the exact failure the fixed basis was introduced to avoid —
 * so the panel NAMES both cycles in its subheading whenever this is rendered.
 * Do not remove that sentence on the grounds that the columns are visible: the
 * columns say which cycles are SHOWN, never which two were SUBTRACTED.
 *
 * Nulls below two selections. ⚠️ **The caller must not render a comparison
 * there.** One side against nothing makes every company read as `New`.
 */
export function comparisonPairOf(selected: readonly RevenueCycle[]): {
  current: RevenueCycle | null;
  previous: RevenueCycle | null;
} {
  if (selected.length < 2) return { current: null, previous: null };
  return {
    current: selected[selected.length - 1] ?? null,
    previous: selected[selected.length - 2] ?? null,
  };
}

/**
 * 🔴 **THE PARTIAL-CYCLE CAPTION, BACK BECAUSE THE PICKER IS BACK.**
 *
 * `null` — and therefore nothing on screen — while every selected cycle has
 * finished, which is the default view. The moment one has not, this is the one
 * line that stops a Top Movers table being read as a list of lost customers and
 * acted on: somebody telephones an account that is perfectly healthy and has
 * simply not been invoiced yet this year.
 *
 * Measured on production, same data, same currency:
 *
 *     running 2026 - 27 against a complete cycle    22 Declining, 35 Stopped
 *     two complete cycles                           11 Declining, 12 Stopped
 *
 * ⚠️ **ONE LINE, NOT THE OLD AMBER PANEL.** The callout this replaces carried
 * three paragraphs and a button that changed the comparison for you. The button
 * has no job now — the reader has a picker — and the paragraphs went on the
 * owner's instruction. What survives is the sentence that prevents the wrong
 * conclusion, and nothing else.
 *
 * ⚠️ **`?.complete !== true`, NOT `=== false`.** A cycle whose window cannot be
 * measured is not evidence that it finished, and staying silent about a
 * comparison nobody could check is the one thing this must never do. Such a
 * cycle gets the fraction-free wording, because a caption stating a WRONG
 * fraction is worse than one stating none — the number is the half that gets
 * quoted onward.
 *
 * A cycle that has NOT STARTED is included, and it is reachable rather than
 * theoretical: cycles are catalogued out to 2029 - 30. It is worded separately
 * because *every* company reads as Stopped there, and calling that
 * "part-elapsed" would understate it.
 */
export function partialCycleNote(
  selected: readonly RevenueCycle[],
  today: string = todayIso(),
): string | null {
  const clauses: string[] = [];
  for (const cycle of selected) {
    const progress = cycleProgress(cycle, today);
    if (progress?.complete === true) continue;
    if (progress === null) {
      clauses.push(`${cycle.name} has not finished.`);
      continue;
    }
    if (progress.running) {
      clauses.push(
        `${cycle.name} is still running — ${progress.elapsedDays} of ${progress.totalDays} days elapsed.`,
      );
      continue;
    }
    clauses.push(`${cycle.name} has not started, so no invoice can have been billed into it.`);
  }
  if (clauses.length === 0) return null;
  return `${clauses.join(' ')} Declining and Stopped are inflated as a result: a company that has simply not been invoiced yet reads as one that has been lost. That is arithmetic, not a finding.`;
}

/* -------------------------------------------------------------------------
 * The five outcomes
 * --------------------------------------------------------------------- */

/**
 * The order the sections read in, which is the owner's own reading order:
 * *"which company is year on year purchasing more will be at the top, and at
 * the bottom also which are not purchasing, and also which are buying but have
 * stopped."*
 *
 * ⚠️ **`stopped` SITTING LOW IS SAFE ONLY BECAUSE THE SUMMARY TILES ARE
 * ABOVE.** It is the most commercially urgent outcome on this tab, so it is
 * counted, priced and made one click away at the top of the panel. Remove the
 * tiles and this ordering buries the finding the tab exists to surface.
 */
export const DIRECTION_ORDER: readonly MoverDirection[] = ['up', 'new', 'down', 'stopped', 'flat'];

export type DirectionTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

export interface DirectionMeta {
  /** The section heading and the filter option. */
  label: string;
  /** The badge on a row — short enough for a narrow column. */
  badge: string;
  tone: DirectionTone;
  /** One clause saying what the outcome IS, so nobody has to infer it. */
  meaning: string;
  /** What the tile's money figure is a total OF. */
  amountLabel: string;
}

/**
 * ⚠️ **AN EXHAUSTIVE `Record`, NEVER A LOOKUP WITH A FALLBACK.** A sixth
 * direction added to `MoverDirection` and not described here fails the BUILD.
 * A fallback would adopt it into whichever branch happened to be the default
 * and render a real outcome under the wrong word — this codebase's own
 * *a ternary over a union adopts new members into the widest branch* defect,
 * which it has shipped and written up.
 */
export const DIRECTION_META: Readonly<Record<MoverDirection, DirectionMeta>> = {
  up: {
    label: 'Growing',
    badge: 'Growing',
    tone: 'success',
    meaning: 'Bought in both cycles, and more in the later one.',
    amountLabel: 'more than last cycle',
  },
  new: {
    label: 'New',
    badge: 'New',
    tone: 'info',
    meaning: 'Nothing in the earlier cycle, revenue in the later one — a customer won.',
    amountLabel: 'this cycle',
  },
  down: {
    label: 'Declining',
    badge: 'Declining',
    tone: 'warning',
    meaning: 'Bought in both cycles, and less in the later one.',
    amountLabel: 'less than last cycle',
  },
  stopped: {
    label: 'Stopped',
    badge: 'Stopped',
    tone: 'danger',
    meaning: 'Revenue in the earlier cycle and none in the later one — a customer lost.',
    amountLabel: 'last cycle, now nothing',
  },
  flat: {
    label: 'Unchanged',
    badge: 'Unchanged',
    tone: 'neutral',
    meaning: 'Identical to the penny in both cycles — usually one repeated line.',
    amountLabel: 'in each cycle',
  },
};

/* -------------------------------------------------------------------------
 * 🔴 The currency-switch trap
 * --------------------------------------------------------------------- */

/**
 * The other currencies a company was paid in **in the cycle where the selected
 * one shows nothing**.
 *
 * 🔴 **THIS WAS THE SHARPEST TRAP ON THIS TAB, AND CONVERSION CLOSED IT —
 * 2026-09-18.**
 *
 * ⚠️ *SUPERSEDED, the state of the world and not the reasoning: "One currency is
 * shown at a time — a scope decision, not a missing capability … while this tab
 * was deliberately left filtering."* `GET /revenue-tracker` takes a
 * `baseCurrency` now, so a company that switched from dollars to euros has both
 * summed into the base and simply shows its real movement.
 *
 * **The trap, for the response where it is still live:** filtered, such a company
 * reads as **`stopped` under USD and `new` under EUR** — two false findings from
 * one ordinary billing change, and *"stopped"* is the one somebody acts on. ⚠️ So
 * a `stopped` or `new` row carrying this hint **must not be presented as a lost
 * or won customer without qualification.**
 *
 * 🔴 **IT ANSWERS `null` FOR EVERY ROW ON A CONVERTED RESPONSE, AND THAT IS THIS
 * FUNCTION WORKING RATHER THAN BEING BYPASSED.** It asks `revenueFor` for the
 * OTHER currencies, and on a converted response every line states the base — so
 * there are no other currencies to find, because there are none. **The caveat
 * withdraws itself exactly when the thing it warns about stops happening, and
 * returns of its own accord against a server that does not convert.** ⛔ Do not
 * gate it on a `converted` flag; that would take it away on the one response that
 * still needs it.
 *
 * ⚠️ **A LATENT HAZARD WORTH KNOWING: a server that converted the AMOUNTS and
 * left the NATIVE code on each line would make this fire on every dual-currency
 * row while every figure was already in one unit** — a caveat about a split that
 * no longer exists. It is not reachable today, because the server's own
 * `totalFor` withholds a cell total the moment two line currencies disagree, so
 * such a conversion would refuse every mixed cell and be visibly broken rather
 * than quietly wrong.
 *
 * Built from `revenueFor` per currency rather than by reading the row's raw
 * service lines, so it asks the same question, of the same population, in the
 * same way the figures on screen were summed.
 */
export interface CurrencySwitchHint {
  currencies: RevenueCurrency[];
  /** The cycle those currencies were found in. */
  cycleName: string;
  /** Which direction produced the hint — the two read as opposite mistakes. */
  direction: 'stopped' | 'new';
}

export function currencySwitchHint(
  mover: Mover,
  current: RevenueCycle | null,
  previous: RevenueCycle | null,
  currency: RevenueCurrency,
): CurrencySwitchHint | null {
  /*
   * `stopped` is judged against the LATER cycle and `new` against the EARLIER
   * one — the cycle in which the selected currency found nothing. Looking in
   * the other one would answer a different question entirely and would flag
   * every dual-currency company.
   */
  const look =
    mover.direction === 'stopped' ? current : mover.direction === 'new' ? previous : null;
  if (look === null) return null;

  const currencies = REVENUE_CURRENCIES.filter(
    (code) => code !== currency && revenueFor(mover.row, look.name, code) !== null,
  );
  if (currencies.length === 0) return null;
  return {
    currencies: [...currencies],
    cycleName: look.name,
    direction: mover.direction === 'stopped' ? 'stopped' : 'new',
  };
}

/**
 * Every other currency this company has EVER been paid in.
 *
 * A weaker signal than the hint above and a different one: it says the figure
 * on the row is not the whole of what this company has paid, which is true of
 * any dual-currency account whether or not it moved between these two cycles.
 *
 * ⚠️ **EMPTY ON EVERY CONVERTED RESPONSE, for the same reason and with the same
 * ruling as {@link currencySwitchHint}**: every line states the base, so the
 * figure on the row IS the whole of what this company has paid and a mark calling
 * it partial would be false. It follows the data rather than a flag.
 */
export function otherCurrenciesEver(
  row: RevenueTrackerRow,
  currency: RevenueCurrency,
): RevenueCurrency[] {
  return currenciesUsedBy(row).filter((code) => code !== currency);
}

/* -------------------------------------------------------------------------
 * Building the list
 * --------------------------------------------------------------------- */

/** One row of the table: a movement plus the two caveats that qualify it. */
export interface MoverRow {
  mover: Mover;
  /** Stable across renders — the server's own row key. */
  key: string;
  /**
   * What this company was paid in EACH selected cycle, keyed by cycle NAME —
   * one entry per cycle column, including the two the comparison runs over.
   *
   * ⚠️ **KEYED BY NAME RATHER THAN BY POSITION.** The column that renders a
   * cell holds the very `RevenueCycle` this was summed for, so it reads
   * `amounts[cycle.name]` and cannot be off by one. A positional array would
   * look tidier and would go silently wrong the first time a selection changed
   * between building the rows and drawing them — printing one cycle's revenue
   * under another cycle's heading, with every figure real.
   *
   * ⚠️ **`null` IS AN ABSENCE, NOT A ZERO**, exactly as {@link Mover} means it:
   * nothing in THIS currency in that cycle. The table renders it as an em dash.
   */
  amounts: Readonly<Record<string, string | null>>;
  switchHint: CurrencySwitchHint | null;
  otherCurrencies: RevenueCurrency[];
}

/**
 * Every company with a movement, plus its figure in every selected cycle.
 *
 * 🔴 **WHICH COMPANIES APPEAR IS DECIDED BY THE COMPARISON PAIR, NOT BY THE
 * SELECTION.** A company with revenue only in a CONTEXT cycle has no movement
 * between the two compared cycles, so `moverFor` returns `null` and it is not
 * listed. That is forced rather than chosen: every row on this tab belongs to
 * one of the five outcomes, and there is no outcome for *did nothing in either
 * compared cycle*. **It also means adding a third cycle column never changes
 * the row count**, which is the property that keeps the tiles, the caption and
 * the table agreeing.
 *
 * ⚠️ **The panel SAYS SO**, because a reader selecting 2022-23 and finding no
 * row for a company that traded only in 2022-23 would otherwise conclude the
 * data was missing rather than the row was out of scope.
 *
 * ⚠️ **NOTHING HERE RE-DERIVES A MOVEMENT.** `moverFor` decides the pair, and
 * `revenueFor` sums a cycle — the same function, of the same population, in the
 * same way, for the compared cycles and the context ones alike. So a cycle
 * column cannot disagree with the Change column beside it.
 */
export function buildMoverRows(
  rows: readonly RevenueTrackerRow[],
  selected: readonly RevenueCycle[],
  currency: RevenueCurrency,
): MoverRow[] {
  /*
   * Derived here rather than taken as two more arguments: one function decides
   * which two cycles are compared, and every caller that needs to NAME them
   * calls that same function. Passing the pair in alongside the selection would
   * make it possible to render a Change column for one pair while the panel's
   * subheading named another.
   */
  const { current, previous } = comparisonPairOf(selected);

  const built: MoverRow[] = [];
  for (const row of rows) {
    const mover = moverFor(row, current, previous, currency);
    /*
     * `null` means this company took nothing in this currency in EITHER compared
     * cycle. It is not a mover and is not a zero — it simply has no answer to
     * the question this tab asks.
     */
    if (mover === null) continue;

    const amounts: Record<string, string | null> = {};
    for (const cycle of selected) amounts[cycle.name] = revenueFor(row, cycle.name, currency);

    built.push({
      mover,
      key: row.key,
      amounts,
      switchHint: currencySwitchHint(mover, current, previous, currency),
      otherCurrencies: otherCurrenciesEver(row, currency),
    });
  }
  return built;
}

/* -------------------------------------------------------------------------
 * Filters
 * --------------------------------------------------------------------- */

/**
 * A company's billing region as the COLUMN FILTER's value, or `null` when it
 * has none.
 *
 * ⚠️ **KEYED ON `slug`, NEVER ON `name`.** A region's display name is editable;
 * its slug is the join key the rest of this app already matches on.
 * {@link regionLabelOf} is what turns it back into words for the popover.
 *
 * 🔴 **`null` IS RETURNED RATHER THAN A SENTINEL, AND THE CHANGE IS DELIBERATE.**
 * This used to fold a missing region onto a reserved string so it could be one
 * more option in a `<select>`. Under the column model an absent value is a
 * first-class state the shared popover already expresses — *Is empty* — so a
 * sentinel would be a second spelling of a fact the primitive states better,
 * and it would put a value in the list that no row's cell actually holds.
 *
 * ⚠️ **A missing region is ORDINARY, not a fault.** `d_companies.country_id` is
 * nullable and was never backfilled, so a real customer lands here (19 of 122,
 * measured). The cell still PRINTS *No billing region recorded* rather than a
 * blank, because a blank reads as a rendering bug and sends the reader hunting
 * a defect instead of a country.
 */
export function regionKeyOf(row: RevenueTrackerRow): string | null {
  return row.billingRegion?.slug ?? null;
}

/** Slug to display name, for the region funnel's option labels. */
export function regionLabelsFrom(rows: readonly MoverRow[]): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const { mover } of rows) {
    const region = mover.row.billingRegion;
    if (region !== null) labels[region.slug] = region.name;
  }
  return labels;
}

/**
 * One cycle column's key, for {@link ColumnSpec.key} and `headerFor`.
 *
 * 🔴 **ONE DEFINITION, SO THE SPEC AND THE HEADER CANNOT NAME DIFFERENT
 * THINGS.** `useTableFilters.headerFor` answers a key it does not recognise
 * with a PLAIN header — `{ label: key }`, no funnel and no error — so a table
 * drawing `cycle:11` while the panel declared `cycle-11` would render a column
 * headed with a raw key, unfilterable, and nothing would fail. Both sides call
 * this.
 *
 * ⚠️ **KEYED ON `id`, NEVER ON `name`.** The name is what a reader sees and is
 * admin-editable; the id is the join key, and two cycles could in principle be
 * renamed to the same string.
 */
export function cycleColumnKey(cycle: RevenueCycle): string {
  return `cycle:${cycle.id}`;
}

/**
 * The table's column keys, in table order.
 *
 * ⚠️ **ONE LIST, READ BY THREE THINGS** — the `ColumnSpec`s the panel builds,
 * the headers the table draws, and the mobile filter sheet. A column that
 * exists in one of those and not the others is a filter somebody can reach on a
 * phone and not at a desk, or the reverse.
 *
 * ⚠️ *SUPERSEDED, 11 September 2026: a fixed `MOVER_COLUMN_KEYS` const holding
 * `previous` and `current`.* The cycle columns are chosen by the reader now, so
 * the list is a FUNCTION of the selection — and everything that reads it takes
 * the SAME array, computed once in the panel, rather than recomputing it and
 * risking a disagreement.
 *
 * It lives in this `.ts` rather than beside the table for the reason every
 * constant on these tabs does: `react(only-export-components)` warns on a file
 * exporting both a component and something else.
 */
export function moverColumnKeys(selected: readonly RevenueCycle[]): string[] {
  return ['company', 'region', 'outcome', ...selected.map(cycleColumnKey), 'change', 'percent'];
}

/**
 * Narrows a funnel's selected value back to an outcome.
 *
 * The outcome column's filter state is a `string[]` like every other column's,
 * and the summary tiles read and write that same state — so this is the one
 * boundary where it becomes a `MoverDirection` again. Anything unrecognised is
 * *no outcome selected* rather than a throw: a tile lighting up for a value
 * nothing can produce is better than a dashboard that will not render.
 */
export function asMoverDirection(value: string | undefined): MoverDirection | null {
  if (value === undefined) return null;
  return DIRECTION_ORDER.includes(value as MoverDirection) ? (value as MoverDirection) : null;
}

/**
 * What is left of the FILTER BAR once the per-column controls moved onto the
 * table headers.
 *
 * 🔴 **ONE FIELD, AND IT IS THE ONE A COLUMN FUNNEL CANNOT EXPRESS.** Company,
 * billing region, outcome and every ordering are now column filters on the
 * table itself — the owner's instruction, 11 September 2026, and the right
 * shape: a control that narrows a column belongs on that column. The floor does
 * not, because it is a rule ACROSS two columns (see {@link applyMoverFilters}),
 * and no header can own a test whose subject is a pair of cells.
 */
export interface MoverFilters {
  /** Raw text from the input; anything unparsable means no floor. */
  minRevenue: string;
}

export const EMPTY_MOVER_FILTERS: MoverFilters = { minRevenue: '' };

/**
 * How many BAR controls differ from their default — the number
 * `DashboardFilterBar` renders on its collapsed toggle.
 *
 * 🔴 **THIS IS THE ONLY THING STANDING BETWEEN A HIDDEN FILTER AND A SILENT
 * ONE.** A reader looking at a narrowed table who cannot see what narrowed it
 * reads the result as the data — here, that is somebody reporting eleven
 * companies as the whole of what moved this cycle.
 *
 * ⚠️ **A CONTROL COUNTS WHEN IT DIFFERS FROM ITS DEFAULT, NOT WHEN IT HAPPENS
 * TO NARROW ANYTHING.** A minimum-revenue box holding unparsable text filters
 * nothing (see {@link parseFloorAmount}) and still counts — because `Clear` is
 * offered only while this is above zero, and a box the reader cannot reset is
 * worse than a count that is one too high.
 *
 * ⚠️ **IT DOES NOT COUNT THE COLUMN FUNNELS**, which are not in the bar and
 * carry their own signal: `ColumnFilter` FILLS the funnel icon on a filtered
 * column, and the panel says how many of how many companies are shown. Counting
 * them here would attach a number to a `Clear` button that cannot clear them.
 */
export function countActiveAdvanced(filters: MoverFilters): number {
  return filters.minRevenue.trim() !== '' ? 1 : 0;
}

/**
 * The floor as a number, or `null` for no floor.
 *
 * A negative or unparsable entry is *no floor* rather than a refusal: this is a
 * live-filtering box somebody types into a character at a time, and refusing a
 * half-typed value would empty the table under the cursor.
 *
 * ⚠️ **SO THIS RETURNING `null` IS NOT THE SAME QUESTION AS THE BOX BEING
 * EMPTY**, and {@link countActiveAdvanced} deliberately asks the other one. A
 * box holding `abc` filters nothing and still has to be clearable.
 */
export function parseFloorAmount(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * ⚠️ **Parsed to a float ONLY to compare against the floor — never to render.**
 * Every amount that reaches the screen stays the exact decimal string it
 * arrived as. `moverFor` makes the same split for the same reason.
 */
function amountAtLeast(value: string | null, floor: number): boolean {
  if (value === null) return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= floor;
}

/**
 * The floor, applied. Everything else the reader can narrow by is a column
 * funnel and is applied by `useTableFilters` before this runs.
 */
export function applyMoverFilters(rows: readonly MoverRow[], filters: MoverFilters): MoverRow[] {
  const floor = parseFloorAmount(filters.minRevenue);
  if (floor === null) return [...rows];

  return rows.filter(({ mover }) => {
    /*
     * 🔴 **THE FLOOR IS THE LARGER SIDE, NOT THE LATER ONE.** Its job is to stop
     * a one-off tiny customer dominating a percentage ranking — and a company
     * that used to pay 50,000 and now pays nothing has a later side of `null`.
     * Testing the later side alone would hide exactly the lost customers this
     * tab exists to surface, silently, and the shorter table would read as
     * *"nobody has stopped"*.
     */
    if (!amountAtLeast(mover.current, floor) && !amountAtLeast(mover.previous, floor)) {
      return false;
    }
    return true;
  });
}

/* -------------------------------------------------------------------------
 * Ordering — driven from the COLUMN HEADERS
 * --------------------------------------------------------------------- */

/**
 * What the rows are ranked BY.
 *
 * 🔴 **EVERY MEMBER IS A COLUMN THE READER CAN SEE, and that is now the rule
 * rather than a coincidence.** Ordering used to be a grouped `<select>` in the
 * filter bar listing five scales by the question they answered. It is a funnel
 * on the column itself since 11 September 2026, on the owner's instruction that
 * this table filter and sort exactly as the companies list does.
 *
 * ⚠️ **THE ONLY ROUTE INTO THE SORT STATE IS A COLUMN DECLARING A `sortKey`.**
 * `useTableFilters` owns that state and hands `onSort` out only for such a
 * column, so a scale listed here and claimed by no column is a control that
 * silently refuses every click — no error, no request, no console line. That
 * defect has already shipped on `/companies` ("Recently updated" did nothing
 * for a week). {@link MOVER_SORT_KEYS} is what makes it unconstructible here:
 * the column specs are typed against it, so a typo is a BUILD failure.
 *
 * ── 🔴 THE CYCLE COLUMNS ARE DYNAMIC, AND ONLY TWO OF THEM SORT ────────────
 *
 * The reader picks the cycle columns, so there can be eight of them. **The two
 * that sort are the COMPARISON PAIR, under the fixed keys `previous` and
 * `current`; every earlier cycle column declares NO `sortKey` and therefore
 * renders no sort control at all.** That was chosen over a generated
 * `cycle:<id>` key, and the reasons are in order of weight:
 *
 * 1. **`previous` and `current` are `Mover`'s OWN fields**, which is what
 *    {@link sortMoverRows} reads. A context cycle has no field on `Mover` at
 *    all, so a key naming one would have to be resolved back to a cycle and
 *    summed from somewhere else — a SECOND source of truth for what a cycle
 *    column is worth, sitting beside the figure the cell already prints.
 * 2. **The sections are ranked by {@link magnitude}, which is defined against
 *    the pair.** Ordering a section by a cycle that played no part in putting
 *    its rows there ranks by a figure unrelated to the grouping — and `stopped`
 *    and `new` would be ordered by a cycle neither outcome is about.
 * 3. **A generated key typechecks while naming a cycle nobody selected.** A
 *    template-literal `cycle:${number}` catches a typo and not that, so the
 *    guard would move from the BUILD to a runtime validation — and a key gone
 *    stale (its cycle deselected while sorted by it) falls back to the default
 *    ranking, silently reordering the table. Keeping this union CLOSED means
 *    every key a header can offer is one the comparator handles, checked by
 *    `tsc`.
 *
 * ⚠️ **The accepted cost, stated rather than hidden: a context cycle column
 * can be FILTERED and not SORTED.** That asymmetry has a precedent one column
 * to its left — `outcome` declares no `sortKey` either, because the table is
 * already grouped by it — and it is visible rather than silent: `ColumnFilter`
 * draws no sort section for such a column, so nothing offers a click that does
 * nothing.
 */
export type MoverSort = 'movement' | 'percent' | 'current' | 'previous' | 'company' | 'region';

/**
 * Every scale, as the literal strings the column specs use for `sortKey`.
 *
 * `satisfies` both ways round on purpose: the array may hold nothing that is
 * not a `MoverSort`, and {@link isMoverSort} narrows back to the union — so the
 * list and the type cannot drift apart in either direction.
 */
export const MOVER_SORT_KEYS = [
  'company',
  'region',
  'previous',
  'current',
  'movement',
  'percent',
] as const satisfies readonly MoverSort[];

function isMoverSort(value: string): value is MoverSort {
  return (MOVER_SORT_KEYS as readonly string[]).includes(value);
}

/**
 * Which end of the scale leads.
 *
 * 🔴 **THE DIRECTION IS NOT A PREFERENCE — IT CHANGES THE QUESTION.**
 * *Biggest movers first* asks **who moved most**. *Smallest first* asks **who
 * is slipping quietly**, and that finding is invisible in a largest-first
 * ranking precisely because the interesting rows are the ones a reader never
 * scrolls to. Both are one click apart in every column's funnel.
 */
export type MoverSortDirection = 'desc' | 'asc';

/**
 * What the table is ordered by.
 *
 * 🔴 **A DISCRIMINATED UNION, NOT A WIDENED `MoverSort`** — added 2026-09-11
 * when every cycle column became sortable, and the shape is the whole guard.
 *
 * {@link sortMoverRows} switches on `kind`, and on `sort` EXHAUSTIVELY within
 * the scale arm, so a new member of either is a BUILD failure rather than a
 * scale nothing ranks by. That is the property the closed `MoverSort` union
 * already bought, kept while adding a scale whose identity is a runtime value.
 *
 * ⚠️ **`MoverSort` IS STILL CLOSED AND MUST STAY SO.** The tempting fix was a
 * generated sort key typed as a plain string, which typechecks while naming a
 * cycle nobody selected — moving the guard from the compiler to a runtime
 * check. Here the cycle is carried in its OWN member with its own field, so
 * the six fixed scales stay compiler-checked and the dynamic one is resolved
 * explicitly by {@link moverOrderFrom}.
 */
export type MoverOrder =
  | { kind: 'scale'; sort: MoverSort; direction: MoverSortDirection }
  /** `cycleName` is the cycle's own name — the key `MoverRow.amounts` uses. */
  | { kind: 'cycle'; cycleName: string; direction: MoverSortDirection };

/**
 * Biggest movement first — the ranking the tab opens on, and the one the
 * owner's brief describes: *"which company is year on year purchasing more
 * will be at the top."*
 */
export const DEFAULT_MOVER_ORDER: MoverOrder = {
  kind: 'scale',
  sort: 'movement',
  direction: 'desc',
};

/**
 * The `sortKey` a CONTEXT cycle column declares.
 *
 * ⚠️ **Keyed on the cycle's NAME, not its id**, because that is what
 * `MoverRow.amounts` is keyed by — so the value the comparator reads is the
 * exact value the cell prints, and a cycle column cannot disagree with itself.
 * Cycle ids are also not in date order in this system, which makes the name the
 * more legible half of a debugging session.
 */
export function cycleSortKey(cycle: RevenueCycle): string {
  return `cycle:${cycle.name}`;
}

/**
 * The ordering a column header has asked for, or the default.
 *
 * ⚠️ **TOTAL, AND FALLS BACK RATHER THAN THROWING.** `useTableFilters` types its
 * sort key as a plain `string` — it is the API's sort key on every other table
 * in this app — so this is the one boundary where it becomes a `MoverSort`. A
 * key no scale here understands lands the reader on the default ranking rather
 * than on an exception or an unsorted table.
 *
 * ⚠️ **THAT FALLBACK CANNOT HIDE A DEAD SORT CONTROL, and it is worth saying
 * why.** The sort UI renders only for a column declaring a `sortKey`, so an
 * unrecognised key cannot come from a header this table drew — only from a
 * future caller. The guard against a dead control is {@link MOVER_SORT_KEYS}
 * typing the specs, at build time, not this function at run time.
 */
export function moverOrderFrom(
  sort: { key: string; direction: MoverSortDirection } | null,
  selected: readonly RevenueCycle[] = [],
): MoverOrder {
  if (sort === null) return DEFAULT_MOVER_ORDER;
  if (isMoverSort(sort.key)) return { kind: 'scale', sort: sort.key, direction: sort.direction };

  /*
   * ⚠️ **A CYCLE KEY IS RESOLVED AGAINST THE LIVE SELECTION, AND AN
   * UNRESOLVABLE ONE FALLS BACK TO THE DEFAULT RANKING.**
   *
   * This is the one runtime check this module accepts, and it is accepted
   * because the failure it guards is VISIBLE rather than silent: the only way
   * to strand a cycle key is to DESELECT the cycle being sorted by, which
   * removes that column from the table. A reader who takes a column away and
   * sees the ranking return to its default has watched the cause and the effect
   * in one gesture — nothing is quietly ordered by a figure no longer on
   * screen, and no sort indicator is left pointing at a heading that is gone.
   *
   * ⚠️ Falling back rather than KEEPING the order is deliberate. Ranking by a
   * cycle whose column has been removed orders the table by a number the reader
   * can no longer see, which is the same lie in a quieter place.
   */
  const prefix = 'cycle:';
  if (!sort.key.startsWith(prefix)) return DEFAULT_MOVER_ORDER;
  const cycleName = sort.key.slice(prefix.length);
  if (!selected.some((cycle) => cycle.name === cycleName)) return DEFAULT_MOVER_ORDER;
  return { kind: 'cycle', cycleName, direction: sort.direction };
}

/**
 * Ranks two rows by ONE cycle's revenue.
 *
 * 🔴 **AN EMPTY CYCLE SINKS TO THE FOOT IN BOTH DIRECTIONS**, exactly as a null
 * percentage and a missing billing region already do in this file. A company
 * with no revenue in that cycle is not worth zero — it is **not on that scale at
 * all** — and this is the column where that case is COMMON rather than rare:
 * measured on production, the 2022-23 column holds 9 valued rows against 23
 * blanks in the Growing group alone.
 *
 * ⚠️ **The subtle half is that reversing the scale must not move them.** The
 * obvious implementation flips the whole comparator, which floats every blank
 * to the TOP of an ascending ranking as though it were the smallest figure on
 * offer. It is no figure. Blanks stay at the foot whichever way the scale
 * points, ranked among themselves by {@link magnitude} so their order is stable
 * rather than arbitrary.
 */
function compareCycleAmount(
  a: MoverRow,
  b: MoverRow,
  cycleName: string,
  direction: MoverSortDirection,
): number {
  const left = a.amounts[cycleName] ?? null;
  const right = b.amounts[cycleName] ?? null;
  if (left !== null && right !== null) {
    const leftValue = num(left);
    const rightValue = num(right);
    if (leftValue !== rightValue) {
      return direction === 'desc' ? rightValue - leftValue : leftValue - rightValue;
    }
    return magnitude(b.mover) - magnitude(a.mover);
  }
  if (left !== null) return -1;
  if (right !== null) return 1;
  return magnitude(b.mover) - magnitude(a.mover);
}

function num(value: string | null): number {
  if (value === null) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * How big this movement is, for ranking inside a section.
 *
 * ⚠️ **Each section is ranked by the figure it actually HAS.** `up` and `down`
 * have a change; `stopped` has only the revenue it has lost, `new` only the
 * revenue it has won. Ranking every section by `change` would order `stopped`
 * and `new` arbitrarily — both are `null` there by design — and the largest
 * lost account would sit wherever the row order happened to put it.
 */
function magnitude(mover: Mover): number {
  switch (mover.direction) {
    case 'up':
    case 'down':
      return Math.abs(num(mover.change));
    case 'stopped':
      return num(mover.previous);
    case 'new':
    case 'flat':
      return num(mover.current);
  }
}

/**
 * 🔴 **A `null` PERCENTAGE IS NOT ON THE SCALE, SO IT SINKS TO THE FOOT — IN
 * BOTH DIRECTIONS.**
 *
 * `new` and `stopped` carry no percentage at all, and neither does a row whose
 * earlier cycle was exactly `0.00`. Scoring those as `0` is the silent wrong
 * answer this module is written against: it files a customer lost outright
 * between two companies that barely moved.
 *
 * ⚠️ **THE SUBTLE HALF IS THAT REVERSING THE SCALE MUST NOT MOVE THEM.** The
 * obvious implementation flips the whole comparator, which floats every
 * no-percentage row to the TOP of an ascending ranking — reading as though they
 * had scored the largest decrease on offer. They scored nothing. They are off
 * the scale at both ends, so they stay at the foot whichever way it points,
 * ranked among themselves by the money they do have.
 */
function comparePercent(a: Mover, b: Mover, direction: MoverSortDirection): number {
  const left = a.percent;
  const right = b.percent;
  if (left !== null && right !== null) {
    if (left !== right) return direction === 'desc' ? right - left : left - right;
    return magnitude(b) - magnitude(a);
  }
  if (left !== null) return -1;
  if (right !== null) return 1;
  return magnitude(b) - magnitude(a);
}

/**
 * ⚠️ **`null` AND A TRUE `0.00` TIE ON THE TWO REVENUE SCALES, and that is the
 * one place they may.** A `stopped` company has no later figure and a company
 * billed exactly nothing has a zero one; ordered by *revenue, later cycle* both
 * belong at the nothing end together. Nothing is RENDERED from this — every
 * amount on screen stays the exact decimal string it arrived as, and the em
 * dash that keeps the two apart is drawn from `mover.current` itself.
 */
export function sortMoverRows(rows: readonly MoverRow[], order: MoverOrder): MoverRow[] {
  const { direction } = order;
  const flip = direction === 'asc' ? -1 : 1;
  const sorted = [...rows];

  /*
   * The dynamic scale is handled BEFORE the closed one, so the switch below
   * stays exhaustive over `MoverSort` with no `default` arm to swallow a member
   * somebody adds later.
   */
  if (order.kind === 'cycle') {
    const { cycleName } = order;
    sorted.sort((a, b) => compareCycleAmount(a, b, cycleName, direction));
    return sorted;
  }

  const { sort } = order;
  sorted.sort((a, b) => {
    switch (sort) {
      case 'company':
        /*
         * The two scales whose natural reading is ASCENDING: A to Z is what
         * `asc` means on a name, where on every numeric scale below it means
         * *smallest first*. The funnel says "Ascending"/"Descending" and both
         * readings are correct for their own column.
         */
        return flip * b.mover.row.company.name.localeCompare(a.mover.row.company.name);
      case 'region': {
        /*
         * ⚠️ **A COMPANY WITH NO BILLING REGION SORTS LAST IN BOTH DIRECTIONS**,
         * for the same reason a null percentage does: it is not on the scale.
         * Sorted as an empty string it would head every ascending list as
         * though it were the first region alphabetically, which reads as a
         * region whose name failed to render.
         */
        const left = a.mover.row.billingRegion?.name ?? null;
        const right = b.mover.row.billingRegion?.name ?? null;
        if (left === null && right === null) {
          return b.mover.row.company.name.localeCompare(a.mover.row.company.name) * -1;
        }
        if (left === null) return 1;
        if (right === null) return -1;
        /* Ties inside a region read alphabetically rather than arbitrarily. */
        const byRegion = flip * right.localeCompare(left);
        return byRegion !== 0
          ? byRegion
          : a.mover.row.company.name.localeCompare(b.mover.row.company.name);
      }
      case 'percent':
        return comparePercent(a.mover, b.mover, direction);
      case 'current':
        return flip * (num(b.mover.current) - num(a.mover.current));
      case 'previous':
        return flip * (num(b.mover.previous) - num(a.mover.previous));
      case 'movement':
        return flip * (magnitude(b.mover) - magnitude(a.mover));
    }
  });
  return sorted;
}

/* -------------------------------------------------------------------------
 * Sections and totals
 * --------------------------------------------------------------------- */

export interface MoverSection {
  direction: MoverDirection;
  rows: MoverRow[];
}

/**
 * ⚠️ **THE ORDERING IS APPLIED WITHIN EACH SECTION, NEVER ACROSS THEM.** The
 * five outcomes do not share a scale — `stopped` and `new` have no change and
 * no percentage at all — so one ranking over the lot would order most of the
 * table by whatever a fallback happened to score it at. That is the reasoning
 * in this module's own header, and offering the reader a flat ranking would
 * quietly overturn it.
 *
 * A section with no rows is dropped, so an outcome nobody landed in does not
 * render an empty band. **The TILES still count it** — that is what keeps
 * *"nobody stopped"* distinguishable from *"this tab does not show who
 * stopped"*.
 */
export function groupByDirection(rows: readonly MoverRow[], order: MoverOrder): MoverSection[] {
  return DIRECTION_ORDER.map((direction) => ({
    direction,
    rows: sortMoverRows(
      rows.filter((row) => row.mover.direction === direction),
      order,
    ),
  })).filter((section) => section.rows.length > 0);
}

export interface DirectionSummary {
  direction: MoverDirection;
  count: number;
  /**
   * The money this outcome is worth, or `null` when there is nothing to add.
   *
   * ⚠️ **`null` IS NOT ZERO** and must never render as `0.00`. It means no
   * company landed in this outcome at all.
   */
  amount: string | null;
  /** How many of these rows carry a currency-switch caveat. */
  switchHints: number;
}

/**
 * The five tiles.
 *
 * ⚠️ **A DIFFERENT FIGURE PER OUTCOME, DELIBERATELY.** `up` and `down` are
 * worth the change they represent; `stopped` is worth the revenue that has gone
 * and `new` the revenue that has arrived. Totalling `change` across all five
 * would silently score a lost customer at zero — the single most expensive
 * thing this tab could get wrong.
 *
 * ⚠️ **THE FIVE TILES ARE NOT SUMMABLE INTO ONE NUMBER**, because they are
 * totals of different things. Nothing here offers one.
 */
export function summariseDirections(rows: readonly MoverRow[]): DirectionSummary[] {
  return DIRECTION_ORDER.map((direction) => {
    const matching = rows.filter((row) => row.mover.direction === direction);
    const amounts = matching.map(({ mover }) => {
      switch (mover.direction) {
        case 'up':
        case 'down':
          return mover.change;
        case 'stopped':
          return mover.previous;
        case 'new':
        case 'flat':
          return mover.current;
      }
    });
    return {
      direction,
      count: matching.length,
      amount: sumDecimalStrings(amounts),
      switchHints: matching.filter((row) => row.switchHint !== null).length,
    };
  });
}

/* -------------------------------------------------------------------------
 * Rendering helpers
 * --------------------------------------------------------------------- */

/**
 * A signed percentage, unclamped.
 *
 * ⚠️ **NEVER CLAMPED.** A `+240%` is the interesting number on this tab, and a
 * ceiling would turn the strongest finding into the same value as an ordinary
 * one. One decimal place under 1,000% and none above it, so a very large move
 * stays readable without pretending to a precision the ratio does not have.
 */
export function formatPercentChange(percent: number): string {
  const sign = percent > 0 ? '+' : '';
  const magnitudeOf = Math.abs(percent);
  const decimals = magnitudeOf >= 1000 ? 0 : 1;
  return `${sign}${percent.toFixed(decimals)}%`;
}

/**
 * A money figure as the owner asked for it on 13 September 2026 — COMPACT, with
 * `K`/`M` and two decimals — together with the exact decimal for its `title`.
 *
 * ⚠️ **ONE COPY, BECAUSE THIS TAB SHOWS MONEY IN FIVE PLACES AND A SIGNED
 * FIGURE IN ONE OF THEM.** The cycle cells, the change cell, the section band,
 * the outcome tiles and the two column funnels all come through here. Formatted
 * at each call site instead, the funnel would read `1,200,000.00` beside a cell
 * reading `£1.20M` and a reader could not match the option to the row it
 * selects.
 *
 * 🔴 **THE SIGN IS SPLIT OFF BEFORE THE SHARED FORMATTER EVER SEES THE NUMBER,
 * AND THAT IS THE WHOLE OF WHY THIS WRAPPER EXISTS.** `Intl`'s compact notation
 * is asked for on a threshold, and every such threshold in this codebase is
 * written `value >= 1000` — which is FALSE for every negative, so a fall of
 * `-340000.00` comes back uncompacted and full-width while the rises beside it
 * compact. Handing it an unsigned magnitude makes that unreachable rather than
 * merely unlikely, and it cannot be reintroduced by an edit to the shared
 * formatter this file does not own.
 *
 * ⚠️ **IT ALSO MAKES A DOUBLED SIGN INEXPRESSIBLE.** The lead is prepended to a
 * head built from a number with no sign of its own, so `+-£340.00K` cannot be
 * produced however the formatter chooses to render a negative.
 *
 * ⚠️ **THE SIGN GOES IN `head`, NOT IN `unit`.** Only `K`/`M` is painted gold —
 * the sign is not a magnitude suffix, it is the direction of the movement, and
 * this tab exists to say which way an account moved. It leads the currency
 * symbol (`-£340.00K`), which is where `en-GB` puts it.
 *
 * ⚠️ **Display only. Never parse one back and never compare two.** `£1.20M` has
 * discarded everything below ten thousand, and on a CHANGE — a difference
 * between two cycles — that discarded remainder is proportionally larger than
 * on either figure it came from. Every caller keeps the exact decimal string.
 */
export interface MoneyFigure {
  /** Render with {@link CompactFigure}; the three pieces carry no whitespace. */
  parts: FigureParts;
  /** The exact stored decimal and its currency code, for the element's `title`. */
  title: string;
}

/**
 * `plusOnPositive` is passed by the caller rather than derived from the sign,
 * because the one figure that carries an explicit `+` only carries it on a
 * `up` row — a `flat` change of exactly `0.00` is not a rise and must not be
 * dressed as one.
 */
export function compactMoney(
  amount: string,
  currency: RevenueCurrency,
  plusOnPositive = false,
): MoneyFigure {
  const trimmed = amount.trim();
  const negative = trimmed.startsWith('-');
  const parts = compactMoneyParts(negative ? trimmed.slice(1) : trimmed, currency);
  const lead = negative ? '-' : plusOnPositive ? '+' : '';
  return {
    parts: { ...parts, head: `${lead}${parts.head}` },
    /*
     * The exact decimal, signed as it is stored. No `+` is invented here: the
     * title is the figure, and the direction is already carried by the row's
     * badge and tone.
     */
    title: `${formatDecimalString(amount)} ${currency}`,
  };
}

/**
 * The same figure as one string, for the two places that can only return text:
 * a column funnel's option label.
 *
 * ⚠️ **Two distinct amounts CAN compact to the same label** — `£1.20M` is every
 * value from 1,195,000 to 1,204,999 — so a funnel can list what looks like one
 * option twice. The option's VALUE is still the exact decimal, so ticking either
 * selects exactly the rows it names and nothing is mis-filtered; what a reader
 * loses is the ability to tell two rows of the list apart. That is accepted
 * against the alternative, which is a funnel formatted differently from the
 * column it filters — the failure the `% change` column already rules on, where
 * the option list is built from the FORMATTED value for exactly this reason.
 */
export function compactMoneyText(amount: string, currency: RevenueCurrency): string {
  return figureText(compactMoney(amount, currency).parts);
}

/*
 * ⛔ **`describeElapsed` WAS DELETED ON 11 SEPTEMBER 2026 AND IS NOT COMING
 * BACK, THOUGH THE CALLOUT IT SERVED DID.**
 *
 * It rendered `9 of 12 months` for a caption reading *"2026 - 27 is still
 * running … most companies will therefore read as declining"*.
 *
 * ⚠️ *SUPERSEDED, hours later the same day: "that callout is gone because
 * {@link latestCompletePair} made it unreachable — this tab now compares two
 * COMPLETE cycles and can never be pointed at a part-elapsed one".* **The owner
 * asked for the cycle picker back, so the tab CAN be pointed at a part-elapsed
 * cycle again**, and the caption is {@link partialCycleNote}.
 *
 * What did not come back is this function. The caption is now ONE LINE on the
 * owner's instruction, and it states the elapsed window in DAYS — the figures
 * {@link cycleProgress} already returns — rather than rounding them into
 * months. A second rendering of the same fact is a second thing to keep in step
 * with the first.
 *
 * The reason it was deleted rather than kept "in case" stands, and this is the
 * case it was written for: a function with no callers is consulted as
 * documentation, and it is the version of the documentation nothing has ever
 * tested. `cycleProgress` is what a caption is built from — it is what
 * `completeCycles` and `partialCycleNote` both decide on, so no two of them can
 * disagree about whether a cycle has ended.
 */
